import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Ticker from "@/components/Ticker";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";

// Same-origin relative path only.
function safeNext(raw: string | null): string | null {
  if (!raw) return null;
  if (!raw.startsWith("/") || raw.startsWith("//")) return null;
  return raw;
}

const AuthPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgot, setIsForgot] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const emailRedirect = next ? `${window.location.origin}${next}` : window.location.origin;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);

    if (isForgot) {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) setError(error.message);
      else setMessage("Check your email for a password reset link.");
    } else if (isSignUp) {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: emailRedirect },
      });
      if (error) setError(error.message);
      else setMessage("Check your email to confirm your account.");
    } else {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) setError(error.message);
      else if (next) window.location.href = next;
      else navigate("/account");
    }
    setLoading(false);
  };


  return (
    <div className="min-h-screen flex flex-col">
      <SEO title="Sign In or Create Account" description="Sign in to your Plymhair account or create one to track orders, save your wishlist and check out faster." url="/auth" />
      <Ticker />
      <Navbar />

      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md bg-card rounded-lg border border-border shadow-sm p-8">
          <h1 className="font-display text-2xl font-bold text-center mb-2">
            {isForgot ? "Reset Password" : isSignUp ? "Create Account" : "Sign In"}
          </h1>
          <p className="text-sm text-muted-foreground text-center font-body mb-8">
            {isForgot
              ? "Enter your email and we'll send you a reset link"
              : isSignUp
              ? "Join PlymHair to track your orders"
              : "Sign in to view your orders and account"}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                maxLength={255}
                placeholder="you@example.com"
              />
            </div>
            {!isForgot && (
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="••••••••"
                />
              </div>
            )}

            {error && (
              <p className="text-sm text-destructive font-body">{error}</p>
            )}
            {message && (
              <p className="text-sm text-green-600 font-body">{message}</p>
            )}

            <Button type="submit" className="w-full" disabled={loading}>
              {loading
                ? "Please wait…"
                : isForgot
                ? "Send Reset Link"
                : isSignUp
                ? "Create Account"
                : "Sign In"}
            </Button>

            {!isSignUp && !isForgot && (
              <button
                type="button"
                onClick={() => {
                  setIsForgot(true);
                  setError("");
                  setMessage("");
                }}
                className="w-full text-center text-sm text-primary hover:underline font-body"
              >
                Forgot your password?
              </button>
            )}
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground font-body">
            {isForgot ? (
              <button
                onClick={() => {
                  setIsForgot(false);
                  setError("");
                  setMessage("");
                }}
                className="text-primary hover:underline"
              >
                Back to Sign In
              </button>
            ) : (
              <>
                {isSignUp ? "Already have an account?" : "Don't have an account?"}{" "}
                <button
                  onClick={() => {
                    setIsSignUp(!isSignUp);
                    setIsForgot(false);
                    setError("");
                    setMessage("");
                  }}
                  className="text-primary hover:underline"
                >
                  {isSignUp ? "Sign In" : "Create Account"}
                </button>
              </>
            )}
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AuthPage;
